export interface VisionCaptureWorkflowPayload {
  withdrawal_date: string;
  material_number: string;
  material_text: string;
  material_type: string | null;
  storage_address: string;
  quantity_removed: string;
  current_stock: string;
  accounting_system: string | null;
  accounting_subarea: string | null;
  accounting_number: string | null;
  accounting_manufacturer: string | null;
  order_info: string | null;
  persons: string[];
  app_version: string;
}

export interface VisionCaptureArtifacts {
  label?: Blob | null;
  source?: Blob | null;
  detectionSource?: Blob | null;
  analysisInput?: Blob | null;
  rectificationInput?: Blob | null;
  ocrWarp?: Blob | null;
  visionFeedback?: Blob | null;
  liveTraining?: Blob | null;
  debugImage?: Blob | null;
  debugData?: Blob | null;
}

export interface VisionCaptureResponse {
  uuid: string;
  case_ref: string;
  display_id: string;
  capture_origin: "vo_bootstrap";
  status: string;
  material_number: string | null;
  created_at: string;
  file_types: string[];
}

function extensionFor(blob: Blob): string {
  if (blob.type.includes("png")) return "png";
  if (blob.type.includes("webp")) return "webp";
  if (blob.type.includes("json")) return "json";
  return "jpg";
}

function appendArtifact(form: FormData, key: string, blob: Blob | null | undefined, baseName: string): void {
  if (!blob || blob.size <= 0) return;
  form.append(key, blob, `${baseName}.${extensionFor(blob)}`);
}

export async function createVisionCaptureCase(input: {
  clientUuid: string;
  workflow: VisionCaptureWorkflowPayload;
  artifacts: VisionCaptureArtifacts;
}): Promise<VisionCaptureResponse> {
  const form = new FormData();
  const prefix = `vo_capture_${input.clientUuid}`;
  const aliases: Record<string, string> = {};
  const seen = new Map<Blob, string>();
  const appendDeduplicated = (key: string, blob: Blob | null | undefined, baseName: string) => {
    if (!blob || blob.size <= 0) return;
    const existing = seen.get(blob);
    if (existing) {
      aliases[key] = existing;
      return;
    }
    seen.set(blob, key);
    appendArtifact(form, key, blob, baseName);
  };
  appendDeduplicated("label", input.artifacts.label, `${prefix}_control`);
  appendDeduplicated("source", input.artifacts.source, `${prefix}_source`);
  appendDeduplicated("detection_source", input.artifacts.detectionSource, `${prefix}_detection_source`);
  appendDeduplicated("analysis_input", input.artifacts.analysisInput, `${prefix}_analysis_input`);
  appendDeduplicated("rectification_input", input.artifacts.rectificationInput, `${prefix}_rectification_input`);
  appendDeduplicated("ocr_warp", input.artifacts.ocrWarp, `${prefix}_ocr_warp`);
  appendDeduplicated("vision_feedback", input.artifacts.visionFeedback, `${prefix}_vision_feedback`);
  appendDeduplicated("live_training", input.artifacts.liveTraining, `${prefix}_live_training`);
  appendDeduplicated("debug", input.artifacts.debugImage, `${prefix}_debug`);
  appendDeduplicated("debug_data", input.artifacts.debugData, `${prefix}_debug_data`);
  form.append("metadata", JSON.stringify({
    client_uuid: input.clientUuid,
    capture_origin: "vo_bootstrap",
    workflow: input.workflow,
    artifact_aliases: aliases,
  }));

  const response = await fetch("/api/admin/vision-captures", {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!response.ok) {
    let message = "VO-Testaufnahme konnte nicht gespeichert werden.";
    try {
      const payload = await response.json() as { detail?: string };
      if (payload.detail) message = payload.detail;
    } catch {
      // HTTP status remains the fallback information.
    }
    throw new Error(message);
  }
  return response.json() as Promise<VisionCaptureResponse>;
}
